import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Typography } from "@mui/material";
import { CreateServiceStyled } from "./create-service.style";
import ServiceGroupForm from "../service-group-form/service-group-form";
import {
  servicesCollection,
  rateCardsCollection,
} from "../../../../services/firebase";
import {
  getNextServiceCardId,
  getNextRolesCardId,
} from "../../../../services/countersService";

export default function CreateServiceGroup({
  initialData,
  onClose,
  onSuccess,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // 1) Build default values, including existing rates
  const makeDefaultValues = () => {
    if (initialData) {
      return {
        name: initialData.name,
        revenueAccount: initialData.revenueAccount,
        roles: initialData.roles.map((r) => ({
          id: r.id,
          name: r.name,
          rate: r.rate,
        })),
      };
    }
    return {
      name: "",
      revenueAccount: "",
      roles: [{ id: undefined, name: "", rate: 0 }],
    };
  };

  // 2) React Hook Form setup
  const form = useForm({ defaultValues: makeDefaultValues() });
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { isSubmitting: formBusy },
  } = form;

  // 3) Reset form whenever initialData changes
  useEffect(() => {
    reset(makeDefaultValues());
  }, [initialData, reset]);

  // 4) onSubmit: dedupe, assign IDs, preserve rates, propagate
  const onSubmit = async () => {
    setSubmitting(true);
    const data = getValues();
    const trimmedName = data.name.trim();
    const revenueAccount = data.revenueAccount.trim();

    // — duplicate group name? —
    const groupsSnap = await servicesCollection.get();
    const nameLC = trimmedName.toLowerCase();
    if (
      groupsSnap.docs.some(
        (d) =>
          d.data().name.trim().toLowerCase() === nameLC &&
          (!initialData || d.id !== String(initialData.id))
      )
    ) {
      alert(
        `Unable to create service group. Another service group with the same name already exists.`
      );
      setSubmitting(false);
      return;
    }

    // — duplicate role names in this group? —
    const allGroupsSnap = await servicesCollection.get();
    const seen = new Set();
    // seed with every existing role name from other groups
    allGroupsSnap.docs.forEach((doc) => {
      if (initialData && String(initialData.id) === doc.id) return;
      const roles = doc.data().roles || [];
      roles.forEach((r) => {
        seen.add(r.name.trim().toLowerCase());
      });
    });

    // now check each entered role
    for (const r of data.roles) {
      const name = r.name.trim();
      const key = name.toLowerCase();
      if (seen.has(key)) {
        alert(
          `Unable to create service group. Another role with the same name already exists.`
        );
        setSubmitting(false);
        return;
      }
      seen.add(key);
    }

    // — 1) decide the group ID —
    const groupId = initialData ? initialData.id : await getNextServiceCardId();

    // — 2) assign role IDs & preserve rates: only generate new IDs for roles without an existing ID —
    const rolesWithIds = await Promise.all(
      data.roles.map(async (r) => {
        const name = r.name.trim();
        const rate = r.rate != null ? Number(r.rate) : 0;
        if (r.id != null) {
          // existing role: keep its id and rate
          return { id: r.id, name, rate };
        }
        // new role: fetch next id once
        const newRoleId = await getNextRolesCardId();
        return { id: newRoleId, name, rate };
      })
    );

    // — 3) write the service‐group —
    const payload = { name: trimmedName, revenueAccount, roles: rolesWithIds };
    try {
      if (initialData) {
        await servicesCollection.doc(String(groupId)).update(payload);
      } else {
        await servicesCollection
          .doc(String(groupId))
          .set({ id: groupId, ...payload });
      }

      // — 4) propagate into all rate cards —
      const updatedGroup = {
        id: groupId,
        name: trimmedName,
        roles: rolesWithIds,
      };
      const rcSnap = await rateCardsCollection.get();
      for (const docSnap of rcSnap.docs) {
        const rc = docSnap.data();
        let newServiceGroups;

        if (initialData) {
          // update existing group: merge old rates
          newServiceGroups = rc.serviceGroups.map((sg) => {
            if (String(sg.id) !== String(groupId)) return sg;
            const oldRoles = sg.roles || [];
            const merged = updatedGroup.roles.map((r) => {
              const exist = oldRoles.find((o) => String(o.id) === String(r.id));
              return exist
                ? { ...exist, name: r.name }
                : { id: r.id, name: r.name, rate: r.rate };
            });
            return { id: groupId, name: updatedGroup.name, roles: merged };
          });
        } else {
          // create: append new group with all rates
          newServiceGroups = [
            ...(rc.serviceGroups || []),
            {
              id: groupId,
              name: trimmedName,
              roles: rolesWithIds.map((r) => ({
                id: r.id,
                name: r.name,
                rate: r.rate,
              })),
            },
          ];
        }

        await rateCardsCollection
          .doc(docSnap.id)
          .update({ serviceGroups: newServiceGroups });
      }

      setJustSaved(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Failed to save service group:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CreateServiceStyled
      onConfirm={handleSubmit(onSubmit)}
      confirmBtnTitle={initialData ? "Update" : "Save"}
      confirmBtnColor="#77E250"
      closeBtnTitle="Cancel"
      onClose={onClose}
      noFooter={justSaved}
      disableConfirmBtn={formBusy || submitting}
    >
      {justSaved ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Typography variant="h5" gutterBottom>
            Congratulations!
          </Typography>
          <Typography>
            Your rate card has been successfully{" "}
            {initialData ? "updated" : "saved"}. A new service group/role has
            been added to all existing rate cards at a rate of $0. Please
            remember to update each card with the correct rate for this role.
          </Typography>
        </div>
      ) : (
        <>
          <Typography variant="h4" gutterBottom>
            {initialData ? "Edit Service Group" : "Add Service Group"}
          </Typography>
          <ServiceGroupForm form={form} isEditMode={!!initialData} />
        </>
      )}
    </CreateServiceStyled>
  );
}
